import { useEffect, useState, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import ErrorBoundary from "./components/ErrorBoundary";
import { Toaster } from "./components/ui/sonner";
import { AudioProvider } from "./contexts/AudioContext";
import GlobalAudioPlayer from "./components/GlobalAudioPlayer";
import { BACKEND_URL } from "./config";
import { apiFetch, setAuthToken, clearAuthToken } from "./utils/api";

// Route-level code splitting — only load pages when needed
const Landing = lazy(() => import("./pages/Landing"));
const Auth = lazy(() => import("./pages/Auth"));
const Roadmap = lazy(() => import("./pages/Roadmap"));
const Feed = lazy(() => import("./pages/Feed"));
const Profile = lazy(() => import("./pages/Profile"));
const Tiers = lazy(() => import("./pages/Tiers"));
const Crates = lazy(() => import("./pages/Crates"));
const CrateDetail = lazy(() => import("./pages/CrateDetail"));
const Search = lazy(() => import("./pages/Search"));
const Analytics = lazy(() => import("./pages/Analytics"));
const TagPage = lazy(() => import("./pages/TagPage"));
const Discover = lazy(() => import("./pages/Discover"));
const ThreadView = lazy(() => import("./pages/ThreadView"));
const ApiKeys = lazy(() => import("./pages/ApiKeys"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));

// Loading fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-black">
    <div className="text-white/40 text-sm animate-pulse">Loading...</div>
  </div>
);

function App() {
  const [token, setToken] = useState(localStorage.getItem('voxvault_token'));
  const [user, setUser] = useState(null);

  useEffect(() => {
    if (token) {
      fetchCurrentUser();
    }
  }, [token]);

  const fetchCurrentUser = async () => {
    try {
      const response = await apiFetch(`${BACKEND_URL}/api/auth/me`);
      if (response.ok) {
        const data = await response.json();
        setUser(data);
      } else {
        logout();
      }
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const login = (newToken, userData) => {
    setAuthToken(newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    clearAuthToken();
    setToken(null);
    setUser(null);
  };

  return (
    <AudioProvider>
      <div className="App">
        <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:bg-white focus:text-black focus:px-4 focus:py-2 focus:rounded">
          Skip to content
        </a>
        <BrowserRouter>
          <main id="main-content">
            <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={!token ? <Landing /> : <Navigate to="/feed" />} />
              <Route path="/roadmap" element={<Roadmap />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/auth" element={!token ? <Auth onLogin={login} /> : <Navigate to="/feed" />} />
              <Route path="/feed" element={token ? <Feed user={user} onLogout={logout} /> : <Navigate to="/auth" />} />
              <Route path="/search" element={token ? <Search user={user} onLogout={logout} /> : <Navigate to="/auth" />} />
              <Route path="/analytics" element={token ? <Analytics user={user} onLogout={logout} /> : <Navigate to="/auth" />} />
              <Route path="/discover" element={token ? <Discover user={user} onLogout={logout} /> : <Navigate to="/auth" />} />
              <Route path="/tag/:tag" element={token ? <TagPage user={user} onLogout={logout} /> : <Navigate to="/auth" />} />
              <Route path="/profile/:username" element={token ? <Profile user={user} onLogout={logout} onUserUpdate={setUser} /> : <Navigate to="/auth" />} />
              <Route path="/thread/:postId" element={token ? <ThreadView user={user} onLogout={logout} /> : <Navigate to="/auth" />} />
              <Route path="/settings/api-keys" element={token ? <ApiKeys user={user} onLogout={logout} /> : <Navigate to="/auth" />} />
              <Route path="/tiers" element={token ? <Tiers user={user} onLogout={logout} onUpgrade={fetchCurrentUser} /> : <Navigate to="/auth" />} />
              <Route path="/crates" element={token ? <Crates user={user} onLogout={logout} /> : <Navigate to="/auth" />} />
              <Route path="/crates/:crateId" element={token ? <CrateDetail user={user} onLogout={logout} /> : <Navigate to="/auth" />} />
              <Route path="/admin/*" element={token && ['moderator', 'admin', 'super_admin'].includes(user?.role) ? <AdminDashboard user={user} onLogout={logout} /> : <Navigate to="/auth" />} />
            </Routes>
            </Suspense>
            </ErrorBoundary>
          </main>
        </BrowserRouter>
        <Toaster />
      </div>
    </AudioProvider>
  );
}

export default App;